import {
  Accordion,
  AccordionCollapse,
  AccordionItem,
  AccordionToggle,
} from '@awaze/design-system.components.accordion'
import { Button } from '@awaze/design-system.components.button'
import { useForm } from '@awaze/design-system.components.form'
import {
  Input,
  InputControl,
  InputValidationMessage,
  Label,
} from '@awaze/design-system.components.input'
import { Stack } from '@awaze/design-system.components.stack'
import { Text } from '@awaze/design-system.components.text'
import { FilterList } from '@awaze/design-system.compositions.filter-list'
import { getBrandConfig } from '@awaze/mfe-common.configs.brand'
import {
  FILTER_BATHROOM_TOGGLE,
  FILTER_BEDROOM_TOGGLE,
  FILTER_GRADE_TOGGLE,
  FILTER_RATING_TOGGLE,
  ONLY_DISCOUNTED_TOGGLE,
  useSearchContextDispatch,
  useSearchContextState,
} from '@awaze/mfe-common.contexts.search'
import { TmsContext } from '@awaze/mfe-common.contexts.tms'
import { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

import { useIntlContext } from '@awaze/mfe-common.contexts.intl'
import { OPTIMIZELY_FLAGS_ENUM } from 'src/constants'
import { useOptimizelyDecisionsContext } from 'src/context/optimizely-decisions-context'
import { useApplicationConfig } from 'src/lib/applicationConfig'
import { useDisplayContext } from 'src/lib/display/displayContext'
import { getFilterTypeArray } from 'src/utils/helpers'
import { isNovasolPoweredBrand } from 'src/utils/isNovasolPoweredBrand'
import {
  handleFilterClick,
  handleMappedFilterClick,
  handleMinMaxPriceFilterKeyPress,
  handlePriceFilterChange,
  handlePriceFilterSubmit,
} from './FilterActions'
import styles from './Filters.module.scss'
import { ClickedFeature } from './Filters.types'
import {
  buildBathroomArray,
  buildBedroomArray,
  buildGradesArray,
  buildReviewsArray,
  buildSelectedFilters,
  containsYear2025,
  containsYear2027,
  getAllFeatures,
  getPopularDestinations,
  getPopularFeatures,
  handleDLEventOnAccordionClick,
  isAccommodationFeature,
  isSiteFeature,
  processFeaturesAndSiteFeatures,
  setFeatureCountsToZero,
} from './Filters.utils'
import { SelectedFilters } from './SelectedFilters'
import { AggregationFeatures } from '@awaze/mfe-common.types-and-mocks.one-search-client'
const PriceSubmitButton = styled(Button)`
  margin-bottom: 0.5rem !important;
`

export const Filters: React.FC = () => {
  const optiContext = useOptimizelyDecisionsContext()
  const { currencySymbol, formatMessage } = useIntlContext()
  const dispatch = useSearchContextDispatch()
  const { brand } = useApplicationConfig()
  const { popularFeatureIds = [] } = getBrandConfig(brand)
  const formHooks = useForm()
  const priceFilterObserver = formHooks.watch
  const [priceRangeError, setPriceRangeError] = useState<boolean>(false)

  const {
    aggregations = null,
    searchCriteria = {},
    hasResults,
    totalSelectedFilters = 0,
    loading,
  } = useSearchContextState()

  const {
    accommodationTypes: selectedAccommodationTypeIds = [],
    features: selectedFeatureIds = [],
    siteFeatures: selectedSiteFeatureIds = [],
    bathrooms = '',
    bedrooms = '',
    grade = '',
    rating = '',
    minPrice,
    maxPrice,
    start = '',
  } = searchCriteria

  const { displayMode } = useDisplayContext()

  const selectedFiltersFromSearchCriteria = [
    ...selectedAccommodationTypeIds,
    ...selectedFeatureIds,
    ...selectedSiteFeatureIds,
  ]

  const aggregationSiteFeatures = aggregations?.siteFeatures ?? []
  const aggregationsFeatures = aggregations?.features ?? []
  const aggregationsAccommodationTypes = aggregations?.accommodationTypes ?? []
  const accommodationTypesAsFeatures = aggregationsAccommodationTypes.map(
    ({ accommodationId, count }) => ({
      featureCode: accommodationId,
      featureId: accommodationId,
      featureName: formatMessage({
        id: `filters.accommodation-types.${accommodationId}`,
        defaultMessage: accommodationId,
      }),
      count,
    }),
  )

  // GD-131: OneSearch V1 VS V2
  const gd131VariationKey = optiContext?.getExperimentVariationKey(
    OPTIMIZELY_FLAGS_ENUM.GD_128_ONESEARCH_V2,
  )

  const isInGd131 = gd131VariationKey === 'variant_a'
  // HACK: GD_128_ONESEARCH_V2 This needs to be removed once OneSearch V2 supports dated features. Ask Blake or Liam for more details

  const hideFreeCancellation =
    isInGd131 && (containsYear2027(start) || containsYear2025(start))

  const combinedAndSortedFeatures = processFeaturesAndSiteFeatures(
    [...aggregationsFeatures, ...accommodationTypesAsFeatures],
    aggregationSiteFeatures,
    hideFreeCancellation,
  )

  const aggregationOfPopularFeatures = aggregations?.popularFeatures ?? []

  const versionedPopularFeatureIds = isInGd131
    ? aggregationOfPopularFeatures.map((feature: AggregationFeatures) => {
        return feature.featureId
      })
    : popularFeatureIds

  const popularFeatures = getPopularFeatures(
    combinedAndSortedFeatures,
    versionedPopularFeatureIds,
  )

  const popularDestinations = getPopularDestinations(combinedAndSortedFeatures)

  const allFeatures = getAllFeatures(combinedAndSortedFeatures)

  const bathroomOptions = buildBathroomArray({
    formatMessage,
    rooms: aggregations?.bathrooms,
  })

  const reviewOptions = buildReviewsArray(aggregations?.reviews)

  const bedroomOptions = buildBedroomArray({
    formatMessage,
    rooms: aggregations?.bedrooms,
  })

  const gradeOptions = buildGradesArray({
    grades: aggregations?.grades || [],
  })

  const displayGradeOptions = buildGradesArray({
    grades: aggregations?.grades || [],
    display: true,
  })

  const selectedFeatures = buildSelectedFilters(
    combinedAndSortedFeatures,
    selectedFiltersFromSearchCriteria,
  )

  const selectedBathrooms = getFilterTypeArray(bathrooms)

  const selectedBedrooms = getFilterTypeArray(bedrooms)

  const selectedGrades = getFilterTypeArray(grade)

  const selectedRatings = getFilterTypeArray(rating)

  const tms = useContext(TmsContext)

  const isUpdating = combinedAndSortedFeatures && loading

  const showPriceFilter = start

  const showExtras = loading || isNovasolPoweredBrand(brand)

  const shouldShowZeroFeaturesCount =
    !hasResults && !loading && displayMode === 'MAP'

  useEffect(() => {
    formHooks.setValue('minPrice', minPrice)
    formHooks.setValue('maxPrice', maxPrice)
  }, [minPrice, maxPrice])

  useEffect(() => {
    const subscription = priceFilterObserver((values) =>
      handlePriceFilterChange(values, setPriceRangeError),
    )
    return () => subscription.unsubscribe()
  }, [priceFilterObserver])

  return (
    <div
      className={`${styles.container} ${isUpdating ? styles.isUpdating : ''}`}
    >
      <Accordion>
        <SelectedFilters
          accommodationTypesAsFeatures={accommodationTypesAsFeatures}
          searchCriteria={searchCriteria}
          selectedFilters={selectedFeatures}
          siteFeatures={aggregationSiteFeatures}
          totalFiltersSelectedCount={totalSelectedFilters}
          dispatch={dispatch}
        />

        {(loading || popularFeatures.length > 0) && (
          <AccordionItem
            open={true}
            accordionToggleCallback={(isOpen) =>
              handleDLEventOnAccordionClick(tms, isOpen, 'popular-features')
            }
          >
            <AccordionToggle>
              {formatMessage({
                id: 'filters.popular-features.heading',
                defaultMessage: 'Popular features',
              })}
            </AccordionToggle>
            <AccordionCollapse>
              <FilterList
                filterGroup="popularFeatures"
                filters={
                  shouldShowZeroFeaturesCount
                    ? setFeatureCountsToZero(popularFeatures)
                    : popularFeatures
                }
                selectedFilters={selectedFiltersFromSearchCriteria}
                aria-label={formatMessage({
                  id: 'filters.popular-features.aria-label',
                  defaultMessage: 'Popular features list',
                })}
                onFilterClick={(featureDetails: ClickedFeature) => {
                  handleFilterClick({
                    allFeatures: combinedAndSortedFeatures,
                    appliedFeatures: selectedFiltersFromSearchCriteria,
                    dispatch,
                    clickedFeature: {
                      featureGroup: formatMessage({
                        id: 'filters.popular-features.feature-group',
                        defaultMessage: 'Popular features',
                      }),
                      featureGroupId: 'popularFeatures',
                      isSiteFeature: isSiteFeature(
                        featureDetails,
                        aggregationSiteFeatures,
                      ),
                      isAccommodationFeature: isAccommodationFeature(
                        featureDetails,
                        accommodationTypesAsFeatures,
                      ),
                      ...featureDetails,
                    },
                    tms,
                    optiContext,
                  })
                }}
              />
            </AccordionCollapse>
          </AccordionItem>
        )}

        {(loading || popularDestinations.length > 0) && (
          <AccordionItem
            open={false}
            accordionToggleCallback={(isOpen) =>
              handleDLEventOnAccordionClick(tms, isOpen, 'popular-destinations')
            }
          >
            <AccordionToggle>
              {formatMessage({
                id: 'filters.popular-destinations',
                defaultMessage: 'Popular destinations',
              })}
            </AccordionToggle>
            <AccordionCollapse>
              <FilterList
                filterGroup="popularDestinations"
                filters={
                  shouldShowZeroFeaturesCount
                    ? setFeatureCountsToZero(popularDestinations)
                    : popularDestinations
                }
                selectedFilters={selectedFiltersFromSearchCriteria}
                aria-label={formatMessage({
                  id: 'filters.popular-destinations-list.aria-label',
                  defaultMessage: 'Popular destinations list',
                })}
                onFilterClick={(featureDetails: ClickedFeature) => {
                  handleFilterClick({
                    allFeatures: combinedAndSortedFeatures,
                    appliedFeatures: selectedFiltersFromSearchCriteria,
                    dispatch,
                    clickedFeature: {
                      featureGroup: 'Popular destinations',
                      isSiteFeature: isSiteFeature(
                        featureDetails,
                        aggregationSiteFeatures,
                      ),
                      featureGroupId: 'popularDestinations',
                      ...featureDetails,
                    },
                    tms,
                    optiContext,
                  })
                }}
              />
            </AccordionCollapse>
          </AccordionItem>
        )}

        {(loading || allFeatures.length > 0) && (
          <AccordionItem
            open={false}
            accordionToggleCallback={(isOpen) =>
              handleDLEventOnAccordionClick(tms, isOpen, 'all-features')
            }
          >
            <AccordionToggle>
              {formatMessage({
                id: 'filters.all-features.heading',
                defaultMessage: 'All features',
              })}
            </AccordionToggle>
            <AccordionCollapse>
              <FilterList
                filterGroup="allFeatures"
                filters={
                  shouldShowZeroFeaturesCount
                    ? setFeatureCountsToZero(allFeatures)
                    : allFeatures
                }
                selectedFilters={selectedFiltersFromSearchCriteria}
                aria-label={formatMessage({
                  id: 'filters.all-features.aria-label',
                  defaultMessage: 'All features list',
                })}
                onFilterClick={(featureDetails: ClickedFeature) => {
                  handleFilterClick({
                    allFeatures: combinedAndSortedFeatures,
                    appliedFeatures: selectedFiltersFromSearchCriteria,
                    dispatch,
                    clickedFeature: {
                      featureGroup: formatMessage({
                        id: 'filters.all-features.feature-group',
                        defaultMessage: 'All features',
                      }),
                      isSiteFeature: isSiteFeature(
                        featureDetails,
                        aggregationSiteFeatures,
                      ),
                      isAccommodationFeature: isAccommodationFeature(
                        featureDetails,
                        accommodationTypesAsFeatures,
                      ),
                      featureGroupId: 'allFeatures',
                      ...featureDetails,
                    },
                    tms,
                    optiContext,
                  })
                }}
              />
            </AccordionCollapse>
          </AccordionItem>
        )}

        {showPriceFilter && (
          <AccordionItem
            open={false}
            accordionToggleCallback={(isOpen) =>
              handleDLEventOnAccordionClick(tms, isOpen, 'price-range')
            }
          >
            <AccordionToggle>
              {formatMessage(
                {
                  id: 'filters.price-range.heading',
                  defaultMessage: 'Price Range ({currency})',
                },
                { currency: currencySymbol },
              )}
            </AccordionToggle>
            <AccordionCollapse>
              <Text sizes={100} mb={12}>
                {formatMessage({
                  id: 'filters.price-range.explanation',
                  defaultMessage:
                    'Enter the minimum and maximum price for the total duration of your stay',
                })}
              </Text>
              <form
                onSubmit={formHooks.handleSubmit((data) =>
                  handlePriceFilterSubmit(data, setPriceRangeError, dispatch),
                )}
                noValidate
              >
                <Stack isHorizontal align="flex-end">
                  <InputControl>
                    <Label htmlFor="minPrice">
                      {formatMessage({
                        id: 'filters.price-range.min',
                        defaultMessage: 'Min',
                      })}
                    </Label>
                    <Input
                      name="minPrice"
                      aria-label={formatMessage({
                        id: 'filters.price-range.aria-min',
                        defaultMessage: 'Minimum Price',
                      })}
                      type="number"
                      formHooks={formHooks}
                      onKeyDown={handleMinMaxPriceFilterKeyPress}
                      status={priceRangeError ? 'error' : 'default'}
                    />
                  </InputControl>
                  <InputControl>
                    <Label htmlFor="maxPrice">
                      {formatMessage({
                        id: 'filters.price-range.max',
                        defaultMessage: 'Max',
                      })}
                    </Label>
                    <Input
                      name="maxPrice"
                      aria-label={formatMessage({
                        id: 'filters.price-range.aria-max',
                        defaultMessage: 'Maximum Price',
                      })}
                      type="number"
                      formHooks={formHooks}
                      onKeyDown={handleMinMaxPriceFilterKeyPress}
                      status={priceRangeError ? 'error' : 'default'}
                    />
                  </InputControl>
                  <PriceSubmitButton
                    text={formatMessage({
                      id: 'filters.price-range.submit',
                      defaultMessage: 'Apply',
                    })}
                    type="submit"
                  />
                </Stack>
                {priceRangeError && (
                  <InputValidationMessage status="error">
                    <Text sizes={100}>
                      {formatMessage({
                        id: 'filters.price-range.validation-message',
                        defaultMessage:
                          "Min value can't be greater than the Max value",
                      })}
                    </Text>
                  </InputValidationMessage>
                )}
              </form>
            </AccordionCollapse>
          </AccordionItem>
        )}

        {(loading || bedroomOptions.length > 0) && (
          <AccordionItem
            open={false}
            accordionToggleCallback={(isOpen) =>
              handleDLEventOnAccordionClick(tms, isOpen, 'bedrooms')
            }
          >
            <AccordionToggle>
              {formatMessage({
                id: 'filters.bedrooms.heading',
                defaultMessage: 'Bedrooms',
              })}
            </AccordionToggle>
            <AccordionCollapse>
              <FilterList
                filterGroup="bedrooms"
                filters={
                  shouldShowZeroFeaturesCount
                    ? setFeatureCountsToZero(bedroomOptions)
                    : bedroomOptions
                }
                selectedFilters={selectedBedrooms}
                aria-label={formatMessage({
                  id: 'filters.bedrooms.aria-label',
                  defaultMessage: 'Bedrooms',
                })}
                onFilterClick={(featureDetails) => {
                  handleMappedFilterClick({
                    allFeatures: bedroomOptions,
                    appliedFeatures: selectedBedrooms,
                    clickedFeature: {
                      featureGroup: formatMessage({
                        id: 'filters.bedrooms.feature-group',
                        defaultMessage: 'Bedrooms',
                      }),
                      featureGroupId: 'bedrooms',
                      ...featureDetails,
                    },
                    dispatch,
                    dispatchType: FILTER_BEDROOM_TOGGLE,
                    tms,
                    optiContext,
                  })
                }}
              />
            </AccordionCollapse>
          </AccordionItem>
        )}

        {(loading || bathroomOptions.length > 0) && (
          <AccordionItem
            open={false}
            accordionToggleCallback={(isOpen) =>
              handleDLEventOnAccordionClick(tms, isOpen, 'bathrooms')
            }
          >
            <AccordionToggle>
              {formatMessage({
                id: 'filters.bathrooms.heading',
                defaultMessage: 'Bathrooms',
              })}
            </AccordionToggle>
            <AccordionCollapse>
              <FilterList
                filterGroup="bathrooms"
                filters={
                  shouldShowZeroFeaturesCount
                    ? setFeatureCountsToZero(bathroomOptions)
                    : bathroomOptions
                }
                selectedFilters={selectedBathrooms}
                aria-label={formatMessage({
                  id: 'filters.bathrooms.aria-label',
                  defaultMessage: 'Bathrooms',
                })}
                onFilterClick={(featureDetails) => {
                  handleMappedFilterClick({
                    allFeatures: bathroomOptions,
                    appliedFeatures: selectedBathrooms,
                    clickedFeature: {
                      featureGroup: formatMessage({
                        id: 'filters.bathrooms.feature-group',
                        defaultMessage: 'Bathrooms',
                      }),
                      featureGroupId: 'bathrooms',
                      ...featureDetails,
                    },
                    dispatch,
                    dispatchType: FILTER_BATHROOM_TOGGLE,
                    tms,
                    optiContext,
                  })
                }}
              />
            </AccordionCollapse>
          </AccordionItem>
        )}

        {(loading || gradeOptions.length > 0) && (
          <AccordionItem
            open={false}
            accordionToggleCallback={(isOpen) =>
              handleDLEventOnAccordionClick(tms, isOpen, 'star-rating')
            }
          >
            <AccordionToggle>
              {formatMessage({
                id: 'filters.star-rating.heading',
                defaultMessage: 'Star rating',
              })}
            </AccordionToggle>
            <AccordionCollapse>
              <Text sizes={100} mb={12}>
                {formatMessage({
                  id: 'filters.star-rating.explanation',
                  defaultMessage:
                    'To indicate quality, the more stars, the more comfort you can expect from the property.',
                })}
              </Text>
              <FilterList
                filterGroup="grades"
                filters={
                  shouldShowZeroFeaturesCount
                    ? setFeatureCountsToZero(displayGradeOptions)
                    : displayGradeOptions
                }
                selectedFilters={selectedGrades}
                aria-label={formatMessage({
                  id: 'filters.star-rating.aria-label',
                  defaultMessage: 'Grades',
                })}
                onFilterClick={({ featureId }) => {
                  const matched = gradeOptions.find(
                    ({ featureId: currentFeatureId }) =>
                      currentFeatureId === featureId,
                  )
                  const clickedFeature = {
                    featureId,
                    featureGroup: formatMessage({
                      id: 'filters.star-rating.feature-group',
                      defaultMessage: 'Star rating',
                    }),
                    featureName: matched ? matched.featureName : '',
                    featureGroupId: 'starRating',
                  } as const

                  handleMappedFilterClick({
                    allFeatures: gradeOptions,
                    appliedFeatures: selectedGrades,
                    clickedFeature,
                    dispatch,
                    dispatchType: FILTER_GRADE_TOGGLE,
                    tms,
                    optiContext,
                  })
                }}
              />
            </AccordionCollapse>
          </AccordionItem>
        )}

        {(loading || reviewOptions.length > 0) && (
          <AccordionItem
            open={false}
            accordionToggleCallback={(isOpen) =>
              handleDLEventOnAccordionClick(tms, isOpen, 'customer-review')
            }
          >
            <AccordionToggle>
              {formatMessage({
                id: 'filters.customer-review.heading',
                defaultMessage: 'Customer review score',
              })}
            </AccordionToggle>
            <AccordionCollapse>
              <FilterList
                filterGroup="reviews"
                filters={
                  shouldShowZeroFeaturesCount
                    ? setFeatureCountsToZero(reviewOptions)
                    : reviewOptions
                }
                selectedFilters={selectedRatings}
                aria-label={formatMessage({
                  id: 'filters.customer-review.aria-label',
                  defaultMessage: 'Customer review score',
                })}
                onFilterClick={(featureDetails) => {
                  handleMappedFilterClick({
                    allFeatures: reviewOptions,
                    appliedFeatures: selectedRatings,
                    clickedFeature: {
                      featureGroup: formatMessage({
                        id: 'filters.customer-review.feature-group',
                        defaultMessage: 'Customer review score',
                      }),
                      featureGroupId: 'customerReviewScore',
                      ...featureDetails,
                    },
                    dispatch,
                    dispatchType: FILTER_RATING_TOGGLE,
                    tms,
                    optiContext,
                  })
                }}
              />
            </AccordionCollapse>
          </AccordionItem>
        )}

        {showExtras && start && (
          <AccordionItem
            open={false}
            accordionToggleCallback={(isOpen) =>
              handleDLEventOnAccordionClick(tms, isOpen, 'extras')
            }
          >
            <AccordionToggle>
              {formatMessage({
                id: 'filters.extras.heading',
                defaultMessage: 'Extras',
              })}
            </AccordionToggle>
            <AccordionCollapse>
              <div className={styles.extrasFilter}>
                <FilterList
                  filterGroup="extras"
                  filters={[
                    {
                      featureId: 1,
                      featureName: formatMessage({
                        id: 'filters.extras.deals',
                        defaultMessage: 'Deals',
                      }),
                      count: 1,
                    },
                  ]}
                  selectedFilters={searchCriteria.discounts ? [1] : []}
                  aria-label={formatMessage({
                    id: 'filters.extras.aria-label',
                    defaultMessage: 'Extras',
                  })}
                  onFilterClick={() => {
                    dispatch({ type: ONLY_DISCOUNTED_TOGGLE })
                  }}
                />
              </div>
            </AccordionCollapse>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  )
}
